/**
 * Cookie Blocker - The Last Hope of Our People to Block The Cookie Offensive
 *
 * @author Evan Cooper <evcooper@wayfair.com>
 * @copyright 2020 Wayfair LLC - All rights reserved
 */

/**
 * Documentation on property descriptors
 * https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Object/getOwnPropertyDescriptor
 *
 * The standardized way of accessing getters and setters is with
 * Object.getOwnPropertyDescriptor, but as the name suggests,
 * it only looks on the objects own properties (it does not look up the prototype chain).
 * document is an instance of HTMLDocument, which inherits from Document.
 * In modern browsers the cookie property is defined on Document.prototype,
 * whereas in older versions of Firefox it is defined on HTMLDocument.prototype.
 *
 * You will notice that we are actually using the prototype of the Document class.
 * This is because the property is defined on the object’s prototype,
 * so that it can be inherited.
 */

/**
 * getCookiePropertyDescriptor
 *
 * @returns {object} the cookie property descriptor object
 */
export function getCookiePropertyDescriptor() {
  return (
    Object.getOwnPropertyDescriptor(Document.prototype, 'cookie') ||
    Object.getOwnPropertyDescriptor(HTMLDocument.prototype, 'cookie')
  );
}

/**
 * getIsFeatureSupported
 *
 * @returns {boolean} returns true if this is a browser that supports object property configuration
 */
export function getIsFeatureSupported() {
  // Dump out if user is in a super old browser (Pre IE9 type of stuff)
  if (!Object.getOwnPropertyDescriptor || !Object.defineProperty) {
    return false;
  }
  const cookieObjectDescription = getCookiePropertyDescriptor();
  // Return true if the description exists, has getters and setters,
  // and is configurable or else this won't work
  return (
    cookieObjectDescription &&
    typeof cookieObjectDescription.get === 'function' &&
    typeof cookieObjectDescription.set === 'function' &&
    cookieObjectDescription.configurable
  );
}

/**
 *
 * @param {array} array array of values
 * @param {*} value any type of value
 * @returns {boolean} returns true if the passed array contains the passed value
 */
export function arrayContains(array, value) {
  return Array.isArray(array) && array.indexOf(value) !== -1;
}

/**
 * resetCookieFunctionality
 *
 * @param {object} cookiePropertyDescriptor the cookie property descriptor
 * @description takes a cookie property descriptor and assigns it to the document object
 */
export function resetCookieFunctionality(cookiePropertyDescriptor) {
  Object.defineProperty(document, 'cookie', {
    configurable: true,
    enumerable: cookiePropertyDescriptor.enumerable,
    get: cookiePropertyDescriptor.get,
    set: cookiePropertyDescriptor.set,
  });
}

/**
 * splitCookiePieces
 *
 * @param {string} cookie a cookie string ("hey=you")
 * @returns {object} returns an object with cookieName and cookieValue properties
 */
export function splitCookiePieces(cookie) {
  let tempCookie = cookie;
  if (!cookie || typeof cookie !== 'string') {
    return {
      cookieName: null,
      cookieValue: null,
    };
  }
  // If the cookie string includes a semi colon, it has fields like expire and domain,
  // We want to isolate just the cookie name and value portion
  if (cookie.includes(';')) {
    const [
      cookieNameAndValue, // Blank space operator on the next line skips unwanted second half of string
      ,
    ] = cookie.split(';');
    tempCookie = cookieNameAndValue;
  }
  const [cookieName, cookieValue] = tempCookie.split('=');
  return {
    cookieName,
    cookieValue,
  };
}

/**
 * cookieBlocker
 *
 * @description overrides the cookie property descriptor to only allow cookies to be set
 * that exist in the allow list array
 * @param {object} object - a param object
 * @param {array} object.allowList - an array of string cookie names that we will allow to be set
 * @param {object} object.logger - an instance of logger
 */
export function cookieBlocker({allowList = [], logger} = {}) {
  // Store the original getter/setter description for preserving the original functionality when cookies are allowed
  const cookiePropertyDescriptor = getCookiePropertyDescriptor();
  // Dump out if the cookie description doesn't have accessor descriptors
  if (!getIsFeatureSupported()) {
    logger.warn({
      name: 'cookieBlocker not supported',
      message: `the cookieBlocker library is not supported`,
    });
    return;
  }

  /**
   * Documentation for Object.defineProperty https://mzl.la/34JQ4C1.
   *
   * This method allows a precise addition to or modification of a property on an object
   * In this case, we are overriding the accessor descriptors of the cookie property
   */
  return Object.defineProperty(document, 'cookie', {
    configurable: true,
    enumerable: cookiePropertyDescriptor.enumerable,
    // Cookie get calls are left undisturbed -
    // we aren't worried about accessing at this point
    get() {
      // Call doesn't require any other arguments here
      // because document.cookie only allows for getting the
      // entire cookie string at one time.
      return cookiePropertyDescriptor.get.call(document);
    },

    set(cookie) {
      const {cookieName, cookieValue} = splitCookiePieces(cookie);
      if (!cookieName) {
        return;
      }
      if (!arrayContains(allowList, cookieName)) {
        logger.warn({
          name: 'Blocked Cookie',
          message: `The cookie "${cookieName}" was blocked by the Cookie Blocker`,
          data: {
            cookieName,
            cookieValue,
          },
        });
        return;
      }
      cookiePropertyDescriptor.set.call(document, cookie);
    },
  });
}
